import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Power, Volume2, VolumeX, ChevronLeft, HelpCircle, X, Upload, FileText, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// VAD Configuration
const VAD_CONFIG = {
  SILENCE_THRESHOLD: 4, // Audio level below this = silence (very low to allow whisper detection)
  SILENCE_DURATION: 1100, // 1.1 s of silence to stop recording (snappier turn-taking)
  MIN_RECORDING_DURATION: 500, // Minimum recording time (ms)
  VOICE_THRESHOLD: 8, // Audio level above this = voice detected (catches soft 'yes')
  INTERRUPT_THRESHOLD: 35, // Audio level to interrupt AI speaking
  LONG_PAUSE_DURATION: 8000, // 8 seconds - ask if user is okay
  NUDGE_MESSAGES: [
    "Hmm, everything okay? Take your time.",
    "I'm listening... go ahead whenever you're ready.",
    "No rush. Let me know when you're ready to continue.",
    "Take a moment if you need. I'm here."
  ]
};

// Voice Metrics Configuration
const VOICE_METRICS_CONFIG = {
  PITCH_SAMPLE_RATE: 100, // Sample pitch every 100ms
  HIGH_PITCH_THRESHOLD: 200, // Hz - above this indicates nervousness
  FAST_SPEECH_WPM: 160, // Words per minute - above this indicates nervousness
  SLOW_SPEECH_WPM: 100, // Words per minute - below this indicates hesitation
  LONG_PAUSE_MS: 800, // Pause longer than this counts as hesitation
};

// Persona configurations with orb colors
const PERSONAS = {
  ms_spark: {
    id: 'ms_spark',
    name: 'Ms. Spark',
    title: 'HR & Culture Head',
    subtitle: 'Warm Personality',
    voice: 'nova',
    orbColors: {
      primary: '#FFD700',
      secondary: '#FF69B4',
      glow: 'rgba(255, 182, 193, 0.6)'
    },
    avatar: 'https://static.prod-images.emergentagent.com/jobs/b75130d9-b2c9-4abe-b7c2-71135ac8a97c/images/91fbe01cf35c48c612075683fb0582cfbea15cade266d04ea6e61fab02b55b7c.png'
  },
  mr_sharp: {
    id: 'mr_sharp',
    name: 'Mr. Sharp',
    title: 'Managing Partner',
    subtitle: 'Stress Interviewer',
    voice: 'onyx',
    orbColors: {
      primary: '#4A0080',
      secondary: '#708090',
      glow: 'rgba(74, 0, 128, 0.6)'
    },
    avatar: 'https://static.prod-images.emergentagent.com/jobs/b75130d9-b2c9-4abe-b7c2-71135ac8a97c/images/8bf06ebf0304c580a0f53d2e88e800d6dc294d6ebc146d2ef5d7b6fdb8f2fa83.png'
  },
  mr_tobby: {
    id: 'mr_tobby',
    name: 'Mr. Tobby',
    title: 'Technical Specialist',
    subtitle: 'Knowledge Expert',
    voice: 'echo',
    orbColors: {
      primary: '#0077B6',
      secondary: '#00B4D8',
      glow: 'rgba(0, 119, 182, 0.6)'
    },
    avatar: 'https://static.prod-images.emergentagent.com/jobs/b75130d9-b2c9-4abe-b7c2-71135ac8a97c/images/e41553c0392cac31f6eea5b5b71e88ce744bf6339dc1e27abb22565cd661e262.png'
  }
};

// Animated Orb Component with enhanced visual feedback
const AnimatedOrb = ({ colors, isSpeaking, isListening, pulseIntensity = 0, voiceActive = false, isInterrupted = false }) => {
  return (
    <div className="relative w-48 h-48 md:w-64 md:h-64">
      {/* Outer glow - expands when listening/speaking */}
      <div 
        className={`absolute inset-0 rounded-full blur-3xl transition-all duration-300`}
        style={{ 
          background: isInterrupted ? 'rgba(239, 68, 68, 0.4)' : colors.glow,
          transform: `scale(${1.2 + pulseIntensity * 0.5 + (isListening ? 0.2 : 0)})`,
          opacity: 0.4 + pulseIntensity * 0.4 + (voiceActive ? 0.2 : 0)
        }}
      />
      
      {/* Ripple rings when listening */}
      {isListening && (
        <>
          <div 
            className="absolute inset-0 rounded-full border-2 animate-ripple-1"
            style={{ borderColor: `${colors.secondary}60` }}
          />
          <div 
            className="absolute inset-0 rounded-full border-2 animate-ripple-2"
            style={{ borderColor: `${colors.secondary}40` }}
          />
          <div 
            className="absolute inset-0 rounded-full border-2 animate-ripple-3"
            style={{ borderColor: `${colors.secondary}20` }}
          />
        </>
      )}
      
      {/* Middle glow ring */}
      <div 
        className="absolute inset-4 rounded-full blur-xl transition-all duration-200"
        style={{ 
          background: `radial-gradient(circle, ${colors.secondary}40, transparent)`,
          transform: `scale(${1 + pulseIntensity * 0.3})`
        }}
      />
      
      {/* Main orb */}
      <div 
        className={`absolute inset-8 rounded-full transition-all duration-150`}
        style={{
          background: isInterrupted 
            ? `radial-gradient(circle at 30% 30%, #ef4444, #dc2626, #b91c1c90)`
            : `radial-gradient(circle at 30% 30%, ${colors.secondary}, ${colors.primary}, ${colors.primary}90)`,
          boxShadow: `
            0 0 ${60 + pulseIntensity * 40}px ${isInterrupted ? 'rgba(239, 68, 68, 0.6)' : colors.glow},
            inset 0 0 30px rgba(255,255,255,0.1),
            inset -10px -10px 30px rgba(0,0,0,0.3)
          `,
          transform: `scale(${1 + pulseIntensity * 0.15})`
        }}
      >
        {/* Inner swirl effect - faster when active */}
        <div 
          className="absolute inset-0 rounded-full"
          style={{
            background: `conic-gradient(from 0deg, transparent, ${colors.secondary}50, transparent, ${colors.primary}50, transparent)`,
            opacity: 0.3 + pulseIntensity * 0.3,
            animation: isSpeaking || voiceActive ? 'spin 2s linear infinite' : 'spin 8s linear infinite'
          }}
        />
        
        {/* Voice wave effect inside orb */}
        {voiceActive && (
          <div className="absolute inset-4 flex items-center justify-center gap-1">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="w-1 bg-white/60 rounded-full animate-voice-bar"
                style={{
                  height: `${20 + pulseIntensity * 60}%`,
                  animationDelay: `${i * 0.1}s`
                }}
              />
            ))}
          </div>
        )}
        
        {/* Interrupt indicator */}
        {isInterrupted && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-white rounded-full flex items-center justify-center">
              <div className="w-4 h-4 bg-white rounded-sm" />
            </div>
          </div>
        )}
        
        {/* Highlight */}
        <div 
          className="absolute top-4 left-6 w-8 h-8 rounded-full opacity-60"
          style={{
            background: 'radial-gradient(circle, rgba(255,255,255,0.8), transparent)'
          }}
        />
      </div>
      
      {/* Listening indicator - green pulse ring */}
      {isListening && !isInterrupted && (
        <div 
          className="absolute inset-2 rounded-full border-4 animate-pulse"
          style={{ borderColor: '#22c55e80' }}
        />
      )}
    </div>
  );
};

// Toggle Switch Component
const ToggleSwitch = ({ label, checked, onChange, tooltip, testId }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="flex items-center gap-1">
        <span className="text-white/80 text-sm">{label}</span>
        <button 
          className="relative"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <HelpCircle className="w-4 h-4 text-white/50 hover:text-white/80" />
          {showTooltip && (
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 rounded-lg text-xs text-white whitespace-nowrap z-50">
              {tooltip}
            </div>
          )}
        </button>
      </div>
      <button 
        onClick={onChange}
        data-testid={testId}
        className={`relative w-16 h-8 rounded-full transition-all duration-300 ${
          checked ? 'bg-blue-600' : 'bg-gray-700'
        }`}
      >
        <span className="absolute left-1 top-1 text-xs text-white/60">On</span>
        <span className="absolute right-1 top-1 text-xs text-white/60">Off</span>
        <div 
          className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow-lg transition-all duration-300 ${
            checked ? 'left-1' : 'left-9'
          }`}
        />
      </button>
    </div>
  );
};

// Persona Card Component
const PersonaCard = ({ persona, isActive, onClick, onHover }) => {
  return (
    <button
      onClick={onClick}
      onMouseEnter={onHover}
      data-testid={`persona-card-${persona.id}`}
      className={`flex-shrink-0 w-64 md:w-72 p-6 rounded-2xl transition-all duration-500 transform
        ${isActive 
          ? 'bg-white/10 border-2 scale-105 shadow-2xl' 
          : 'bg-white/5 border border-white/10 hover:bg-white/10 hover:scale-102'
        }`}
      style={{ 
        borderColor: isActive ? persona.orbColors.primary : undefined,
        boxShadow: isActive ? `0 0 40px ${persona.orbColors.glow}` : undefined
      }}
    >
      <div 
        className="w-20 h-20 mx-auto mb-4 rounded-full overflow-hidden ring-4 transition-all duration-300"
        style={{ ringColor: isActive ? persona.orbColors.primary : 'rgba(255,255,255,0.2)' }}
      >
        <img src={persona.avatar} alt={persona.name} className="w-full h-full object-cover" />
      </div>
      <h3 className="text-xl font-bold text-white mb-1">{persona.name}</h3>
      <p className="text-sm text-white/60">{persona.title}</p>
      <p className="text-xs text-white/40 mt-1">{persona.subtitle}</p>
      <div 
        className="mt-4 h-1 rounded-full mx-auto w-16 transition-all duration-300"
        style={{ 
          background: `linear-gradient(to right, ${persona.orbColors.primary}, ${persona.orbColors.secondary})`,
          opacity: isActive ? 1 : 0.5
        }}
      />
    </button>
  );
};

export default function InterviewBotPage() {
  // Stage: 'start' | 'select' | 'setup' | 'interview' | 'feedback'
  const [stage, setStage] = useState('start');
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [hoveredPersona, setHoveredPersona] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [currentSpeech, setCurrentSpeech] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [interviewTime, setInterviewTime] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [feedbackData, setFeedbackData] = useState(null);
  const [pulseIntensity, setPulseIntensity] = useState(0);
  const [voiceActive, setVoiceActive] = useState(false);
  const [listeningStatus, setListeningStatus] = useState('idle');
  const [isInterrupted, setIsInterrupted] = useState(false);

  // Setup form state
  const [candidateName, setCandidateName] = useState('');
  const [candidateEmail, setCandidateEmail] = useState('');
  const [roleTarget, setRoleTarget] = useState('');
  const [experienceSummary, setExperienceSummary] = useState('');
  const [resumeText, setResumeText] = useState('');
  const [resumeFileName, setResumeFileName] = useState('');
  const [jdText, setJdText] = useState('');
  const [jdFileName, setJdFileName] = useState('');
  const [parsingResume, setParsingResume] = useState(false);
  const [parsingJD, setParsingJD] = useState(false);
  const [docError, setDocError] = useState('');
  // questionMode: 'fixed' (8-10 default question count) | 'custom' (user-pick) | 'open'
  const [questionMode, setQuestionMode] = useState('fixed');
  const [customQuestionCount, setCustomQuestionCount] = useState(10);
  const [questionsAsked, setQuestionsAsked] = useState(0);
  const [questionLimit, setQuestionLimit] = useState(null);
  const [startError, setStartError] = useState('');
  
  // Sentiment tracking
  const [currentSentiment, setCurrentSentiment] = useState(null);
  const [confidenceScore, setConfidenceScore] = useState(null);
  const [sentimentHistory, setSentimentHistory] = useState([]);
  const [voiceAnalysis, setVoiceAnalysis] = useState(null);
  
  // Feature toggles
  const [showTranscript, setShowTranscript] = useState(true);
  const [idealTimeLimit, setIdealTimeLimit] = useState(true);
  
  // Refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const timerRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const recordingStartTimeRef = useRef(null);
  const vadLoopRef = useRef(null);
  const hasSpokenRef = useRef(false);
  const longPauseTimerRef = useRef(null);
  const interruptStreamRef = useRef(null);
  const interruptAnalyserRef = useRef(null);
  const interruptContextRef = useRef(null);
  const interruptLoopRef = useRef(null);
  const listeningStartTimeRef = useRef(null);
  
  // Voice metrics refs
  const voiceMetricsRef = useRef({
    pitchSamples: [],
    volumeSamples: [],
    pauseCount: 0,
    pauseDurations: [],
    speakingDuration: 0,
    totalDuration: 0,
    lastVoiceTime: null,
    inPause: false
  });

  // Timer effect
  useEffect(() => {
    if (stage === 'interview' && !timerRef.current) {
      timerRef.current = setInterval(() => {
        setInterviewTime(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [stage]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopListening();
      stopAudio();
      stopInterruptDetection();
      clearLongPauseTimer();
    };
  }, []);

  // Push-to-Talk state (must be declared BEFORE the SPACE-bar useEffect below to avoid TDZ)
  const [micError, setMicError] = useState('');
  const [pushToTalkMode, setPushToTalkMode] = useState(false);
  const [pttHeld, setPttHeld] = useState(false);
  const pttHeldRef = useRef(false);
  const pushToTalkModeRef = useRef(false);
  useEffect(() => { pushToTalkModeRef.current = pushToTalkMode; }, [pushToTalkMode]);

  // SPACE-bar push-to-talk (only active in interview stage + PTT mode)
  useEffect(() => {
    if (stage !== 'interview' || !pushToTalkMode) return;
    const isTypingInForm = (target) => {
      if (!target) return false;
      const tag = (target.tagName || '').toLowerCase();
      return tag === 'input' || tag === 'textarea' || target.isContentEditable;
    };
    const onKeyDown = (e) => {
      if (e.code !== 'Space' || e.repeat) return;
      if (isTypingInForm(e.target)) return;
      e.preventDefault();
      if (isSpeaking || isProcessing || pttHeldRef.current) return;
      pttHeldRef.current = true;
      setPttHeld(true);
      setMicError('');
      if (!isRecording) startListening();
    };
    const onKeyUp = (e) => {
      if (e.code !== 'Space') return;
      if (isTypingInForm(e.target)) return;
      e.preventDefault();
      if (!pttHeldRef.current) return;
      pttHeldRef.current = false;
      setPttHeld(false);
      if (isRecording) stopListening();
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [stage, pushToTalkMode, isSpeaking, isProcessing, isRecording]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `-${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // Clear long pause timer
  const clearLongPauseTimer = () => {
    if (longPauseTimerRef.current) {
      clearTimeout(longPauseTimerRef.current);
      longPauseTimerRef.current = null;
    }
  };

  // Start long pause timer - triggers nudge if user doesn't speak
  const startLongPauseTimer = () => {
    clearLongPauseTimer();
    listeningStartTimeRef.current = Date.now();
    
    longPauseTimerRef.current = setTimeout(async () => {
      // Only trigger if still listening and user hasn't spoken
      if (isRecording && !hasSpokenRef.current && !isSpeaking && !isProcessing) {
        // Send a nudge message
        const nudgeMessage = VAD_CONFIG.NUDGE_MESSAGES[Math.floor(Math.random() * VAD_CONFIG.NUDGE_MESSAGES.length)];
        
        // Add to messages
        setMessages(prev => [...prev, { role: 'assistant', content: nudgeMessage }]);
        setCurrentSpeech(nudgeMessage);
        
        // Generate and play nudge audio
        try {
          const persona = selectedPersona || PERSONAS.ms_spark;
          const res = await fetch(`${API_URL}/api/interview/preview-voice`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text: nudgeMessage, voice: persona.voice })
          });
          
          if (res.ok) {
            const data = await res.json();
            if (data.audio_base64 && !isMuted) {
              // Stop current listening while playing nudge
              stopListening();
              playAudio(data.audio_base64, true);
            }
          }
        } catch (err) {
          console.error('Nudge audio failed:', err);
          // Restart listening even if audio fails
          startLongPauseTimer();
        }
      }
    }, VAD_CONFIG.LONG_PAUSE_DURATION);
  };

  // Start interrupt detection - monitors mic while AI is speaking
  const startInterruptDetection = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      interruptStreamRef.current = stream;
      
      interruptContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      interruptAnalyserRef.current = interruptContextRef.current.createAnalyser();
      const source = interruptContextRef.current.createMediaStreamSource(stream);
      source.connect(interruptAnalyserRef.current);
      interruptAnalyserRef.current.fftSize = 256;
      
      runInterruptLoop();
    } catch (err) {
      console.error('Interrupt detection setup failed:', err);
    }
  };

  // Run interrupt detection loop
  const runInterruptLoop = () => {
    if (!interruptAnalyserRef.current) return;
    
    const dataArray = new Uint8Array(interruptAnalyserRef.current.frequencyBinCount);
    let voiceDetectedTime = null;
    
    const checkInterrupt = () => {
      if (!interruptAnalyserRef.current || !isSpeaking) {
        stopInterruptDetection();
        return;
      }
      
      interruptAnalyserRef.current.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
      
      // Check if user is trying to speak
      if (average > VAD_CONFIG.INTERRUPT_THRESHOLD) {
        if (!voiceDetectedTime) {
          voiceDetectedTime = Date.now();
        } else if (Date.now() - voiceDetectedTime > 300) {
          // User has been speaking for 300ms - interrupt AI
          handleInterrupt();
          return;
        }
      } else {
        voiceDetectedTime = null;
      }
      
      interruptLoopRef.current = requestAnimationFrame(checkInterrupt);
    };
    
    interruptLoopRef.current = requestAnimationFrame(checkInterrupt);
  };

  // Handle interrupt - stop AI and listen to user
  const handleInterrupt = () => {
    console.log('User interrupted AI!');
    setIsInterrupted(true);
    
    // Stop AI audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsSpeaking(false);
    
    // Stop interrupt detection
    stopInterruptDetection();
    
    // Brief visual feedback
    setTimeout(() => {
      setIsInterrupted(false);
      // Start listening to user
      startListening();
    }, 500);
  };

  // Stop interrupt detection
  const stopInterruptDetection = () => {
    if (interruptLoopRef.current) {
      cancelAnimationFrame(interruptLoopRef.current);
      interruptLoopRef.current = null;
    }
    
    if (interruptStreamRef.current) {
      interruptStreamRef.current.getTracks().forEach(track => track.stop());
      interruptStreamRef.current = null;
    }
    
    if (interruptContextRef.current) {
      interruptContextRef.current.close();
      interruptContextRef.current = null;
    }
  };

  // Play audio with TTS - starts listening after audio ends
  const playAudio = useCallback((base64Audio, shouldListenAfter = true) => {
    if (!base64Audio || isMuted) {
      if (shouldListenAfter && stage === 'interview') {
        setTimeout(() => startListening(), 500);
      }
      return;
    }
    
    // Stop any previously playing audio first to avoid overlap (e.g. hover-preview races)
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch (_) { /* ignore */ }
      audioRef.current = null;
    }

    setIsSpeaking(true);
    setListeningStatus('waiting');
    const audio = new Audio(`data:audio/mp3;base64,${base64Audio}`);
    audioRef.current = audio;
    
    // Start interrupt detection while AI speaks
    startInterruptDetection();
    
    audio.onended = () => {
      setIsSpeaking(false);
      stopInterruptDetection();
      if (shouldListenAfter && stage === 'interview' && !pushToTalkModeRef.current) {
        setTimeout(() => startListening(), 300);
      }
    };
    
    audio.onerror = () => {
      setIsSpeaking(false);
      stopInterruptDetection();
      if (shouldListenAfter && stage === 'interview' && !pushToTalkModeRef.current) {
        setTimeout(() => startListening(), 300);
      }
    };
    
    audio.play().catch(() => {
      setIsSpeaking(false);
      stopInterruptDetection();
      if (shouldListenAfter && stage === 'interview' && !pushToTalkModeRef.current) {
        setTimeout(() => startListening(), 300);
      }
    });
  }, [isMuted, stage]);

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsSpeaking(false);
    stopInterruptDetection();
  };

  // Voice Activity Detection - Start listening with auto-stop on silence
  // (PTT + micError state declared earlier near top to avoid TDZ in SPACE-bar useEffect)

  const startListening = async () => {
    // If we're not actually able to listen right now, retry shortly instead of bailing silently.
    if (isProcessing || isSpeaking) {
      setTimeout(() => { if (stage === 'interview' && !isProcessing && !isSpeaking) startListening(); }, 400);
      return;
    }
    if (isRecording) return; // already listening
    setMicError('');
    
    clearLongPauseTimer();
    
    // Reset voice metrics
    voiceMetricsRef.current = {
      pitchSamples: [],
      volumeSamples: [],
      pauseCount: 0,
      pauseDurations: [],
      speakingDuration: 0,
      totalDuration: 0,
      lastVoiceTime: null,
      inPause: false
    };
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      // Setup audio analyzer for VAD and voice metrics
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      analyserRef.current.fftSize = 2048; // Higher for better pitch detection
      
      // Setup MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      hasSpokenRef.current = false;
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      
      mediaRecorder.onstop = async () => {
        clearLongPauseTimer();
        
        // Calculate final voice metrics
        const metrics = voiceMetricsRef.current;
        metrics.totalDuration = Date.now() - recordingStartTimeRef.current;
        
        if (hasSpokenRef.current && audioChunksRef.current.length > 0) {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          await processAudio(audioBlob, metrics);
        } else {
          setListeningStatus('listening');
          setTimeout(() => {
            if (stage === 'interview' && !isSpeaking && !isProcessing) {
              startListening();
            }
          }, 500);
        }
      };
      
      mediaRecorder.start(100);
      setIsRecording(true);
      setListeningStatus('listening');
      recordingStartTimeRef.current = Date.now();
      
      // Start VAD loop with voice metrics collection
      runVADLoop();
      
      // Start long pause timer
      startLongPauseTimer();
      
    } catch (err) {
      console.error('Microphone error:', err);
      setListeningStatus('idle');
      // Show a user-friendly hint so user can tap "Speak" manually
      if (err && err.name === 'NotAllowedError') {
        setMicError('Microphone access blocked. Please allow mic from your browser bar and tap "Tap to Speak".');
      } else {
        setMicError('Couldn\'t open the microphone. Tap "Tap to Speak" to try again.');
      }
    }
  };

  // VAD Loop - monitors audio levels, detects silence, and collects voice metrics
  const runVADLoop = () => {
    if (!analyserRef.current) return;
    
    const bufferLength = analyserRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    const floatArray = new Float32Array(analyserRef.current.fftSize);
    let silenceStart = null;
    let lastSampleTime = Date.now();
    
    // Simple pitch detection using autocorrelation
    const detectPitch = (buffer, sampleRate) => {
      let maxCorr = 0;
      let foundPitch = 0;
      
      // Look for pitch between 80Hz and 400Hz
      const minPeriod = Math.floor(sampleRate / 400);
      const maxPeriod = Math.floor(sampleRate / 80);
      
      for (let period = minPeriod; period < maxPeriod; period++) {
        let corr = 0;
        for (let i = 0; i < buffer.length - period; i++) {
          corr += Math.abs(buffer[i] - buffer[i + period]);
        }
        corr = 1 - corr / buffer.length;
        if (corr > maxCorr) {
          maxCorr = corr;
          foundPitch = sampleRate / period;
        }
      }
      
      return maxCorr > 0.5 ? foundPitch : 0;
    };
    
    const checkAudio = () => {
      if (!analyserRef.current || !isRecording) return;
      
      const now = Date.now();
      const metrics = voiceMetricsRef.current;
      
      // Get frequency data for volume
      analyserRef.current.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
      
      setPulseIntensity(average / 255);
      
      const isVoice = average > VAD_CONFIG.VOICE_THRESHOLD;
      setVoiceActive(isVoice);
      
      // Collect voice metrics
      if (isVoice) {
        hasSpokenRef.current = true;
        
        // Track speaking duration
        if (metrics.lastVoiceTime) {
          metrics.speakingDuration += now - metrics.lastVoiceTime;
        }
        metrics.lastVoiceTime = now;
        
        // Sample pitch every 100ms
        if (now - lastSampleTime >= VOICE_METRICS_CONFIG.PITCH_SAMPLE_RATE) {
          analyserRef.current.getFloatTimeDomainData(floatArray);
          const pitch = detectPitch(floatArray, audioContextRef.current.sampleRate);
          if (pitch > 0) {
            metrics.pitchSamples.push(pitch);
          }
          metrics.volumeSamples.push(average);
          lastSampleTime = now;
        }
        
        // Check if coming out of a pause
        if (metrics.inPause) {
          metrics.inPause = false;
        }
        
        silenceStart = null;
        startLongPauseTimer();
        
        if (silenceTimerRef.current) {
          clearTimeout(silenceTimerRef.current);
          silenceTimerRef.current = null;
        }
      } else if (average < VAD_CONFIG.SILENCE_THRESHOLD) {
        // Track pause if we were speaking
        if (metrics.lastVoiceTime && !metrics.inPause && hasSpokenRef.current) {
          const pauseStart = now;
          if (!silenceStart) {
            silenceStart = pauseStart;
          }
          
          const pauseDuration = now - silenceStart;
          
          // Count as pause if longer than threshold
          if (pauseDuration >= VOICE_METRICS_CONFIG.LONG_PAUSE_MS && !metrics.inPause) {
            metrics.pauseCount++;
            metrics.pauseDurations.push(pauseDuration);
            metrics.inPause = true;
          }
        }
        
        if (!silenceStart) {
          silenceStart = now;
        }
        
        const silenceDuration = now - silenceStart;
        const recordingDuration = now - recordingStartTimeRef.current;
        
        if (hasSpokenRef.current && 
            silenceDuration >= VAD_CONFIG.SILENCE_DURATION && 
            recordingDuration >= VAD_CONFIG.MIN_RECORDING_DURATION &&
            !pttHeldRef.current) {  // never auto-stop while user is holding PTT
          stopListening();
          return;
        }
      }
      
      vadLoopRef.current = requestAnimationFrame(checkAudio);
    };
    
    vadLoopRef.current = requestAnimationFrame(checkAudio);
  };

  // Stop listening and cleanup
  const stopListening = () => {
    clearLongPauseTimer();
    
    if (vadLoopRef.current) {
      cancelAnimationFrame(vadLoopRef.current);
      vadLoopRef.current = null;
    }
    
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    
    setIsRecording(false);
    setVoiceActive(false);
    setPulseIntensity(0);
    setListeningStatus('processing');
  };

  // Process audio with voice metrics
  const processAudio = async (audioBlob, metrics = null) => {
    setIsProcessing(true);
    setListeningStatus('processing');
    
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      
      const transcribeRes = await fetch(`${API_URL}/api/interview/transcribe`, {
        method: 'POST',
        body: formData
      });
      
      if (!transcribeRes.ok) throw new Error('Transcription failed');
      const { text } = await transcribeRes.json();
      
      if (!text || text.trim().length === 0) {
        setIsProcessing(false);
        setListeningStatus('listening');
        setTimeout(() => startListening(), 500);
        return;
      }
      
      // Calculate voice metrics summary
      let voiceMetrics = null;
      if (metrics && metrics.pitchSamples.length > 0) {
        const avgPitch = metrics.pitchSamples.reduce((a, b) => a + b, 0) / metrics.pitchSamples.length;
        const avgVolume = metrics.volumeSamples.length > 0 
          ? metrics.volumeSamples.reduce((a, b) => a + b, 0) / metrics.volumeSamples.length 
          : 0;
        
        // Calculate words per minute (approximate: 5 chars = 1 word)
        const wordCount = text.split(/\s+/).length;
        const speakingSeconds = metrics.speakingDuration / 1000;
        const wordsPerMinute = speakingSeconds > 0 ? (wordCount / speakingSeconds) * 60 : 0;
        
        voiceMetrics = {
          average_pitch: Math.round(avgPitch),
          average_volume: Math.round(avgVolume),
          words_per_minute: Math.round(wordsPerMinute),
          pause_count: metrics.pauseCount,
          total_pause_duration: metrics.pauseDurations.reduce((a, b) => a + b, 0),
          speaking_duration: Math.round(metrics.speakingDuration),
          total_duration: Math.round(metrics.totalDuration)
        };
      }
      
      setMessages(prev => [...prev, { role: 'user', content: text }]);
      
      // Send chat with voice metrics
      const chatRes = await fetch(`${API_URL}/api/interview/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          session_id: sessionId, 
          user_text: text,
          voice_metrics: voiceMetrics
        })
      });
      
      if (!chatRes.ok) throw new Error('Chat failed');
      const chatData = await chatRes.json();
      
      // Update sentiment data
      if (chatData.sentiment) {
        setCurrentSentiment(chatData.sentiment);
        setConfidenceScore(chatData.confidence_score);
        setSentimentHistory(prev => [...prev, {
          sentiment: chatData.sentiment,
          confidence: chatData.confidence_score
        }]);
      }
      
      setCurrentSpeech(chatData.response_text);
      setMessages(prev => [...prev, { role: 'assistant', content: chatData.response_text }]);
      if (typeof chatData.questions_asked === 'number') setQuestionsAsked(chatData.questions_asked);
      if (chatData.question_limit) setQuestionLimit(chatData.question_limit);
      
      // Update voice analysis display
      if (chatData.voice_analysis) {
        setVoiceAnalysis(chatData.voice_analysis);
      }
      
      if (chatData.is_ended) {
        setTimeout(() => getFeedback(), 1500);
      } else if (chatData.audio_base64) {
        playAudio(chatData.audio_base64, true);
      } else {
        setTimeout(() => startListening(), 500);
      }
    } catch (err) {
      console.error('Processing error:', err);
      setTimeout(() => startListening(), 1000);
    } finally {
      setIsProcessing(false);
    }
  };

  // Tracks the most-recently hovered persona id; older preview-fetches are ignored when they resolve
  const introHoverIdRef = useRef(null);

  // Play intro audio for hovered persona — only the latest hover wins
  const playIntroAudio = async (persona) => {
    if (isMuted) return;
    introHoverIdRef.current = persona.id;
    const requestedId = persona.id;
    // Immediately stop whatever was playing so we don't stack voices
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch (_) { /* ignore */ }
      audioRef.current = null;
      setIsSpeaking(false);
    }
    try {
      const introText = `Hi, I'm ${persona.name}. ${persona.title}. Click to start your mock interview with me.`;
      const res = await fetch(`${API_URL}/api/interview/preview-voice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: introText, voice: persona.voice })
      });
      if (!res.ok) return;
      const data = await res.json();
      // If user has hovered onto a different card while we waited, drop this audio
      if (introHoverIdRef.current !== requestedId) return;
      if (data.audio_base64) {
        playAudio(data.audio_base64, false);
      }
    } catch (err) {
      console.error('Preview audio failed:', err);
    }
  };

  // Parse uploaded doc (resume or JD)
  const handleFileUpload = async (file, kind) => {
    if (!file) return;
    setDocError('');
    if (kind === 'resume') { setParsingResume(true); setResumeFileName(file.name); }
    else { setParsingJD(true); setJdFileName(file.name); }
    try {
      const fd = new FormData();
      fd.append('file', file, file.name);
      const res = await fetch(`${API_URL}/api/interview/parse-doc`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setDocError(data.detail || data.message || 'Could not parse file. You can paste the text instead.');
      } else if (kind === 'resume') {
        setResumeText(data.text || '');
      } else {
        setJdText(data.text || '');
      }
    } catch (err) {
      setDocError('Upload failed. Please try again or paste the text.');
    } finally {
      setParsingResume(false);
      setParsingJD(false);
    }
  };

  // Start interview session (called from Setup screen)
  const startInterview = async (persona) => {
    setIsProcessing(true);
    setSelectedPersona(persona);
    setStartError('');

    // Resolve question limit
    let q_limit = null;
    if (questionMode === 'fixed') q_limit = 10;
    else if (questionMode === 'custom') q_limit = Math.max(3, Math.min(25, parseInt(customQuestionCount, 10) || 10));
    // 'open' => null

    // Small retry loop for transient 502s from preview ingress
    let res, lastErr;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        res = await fetch(`${API_URL}/api/interview/start`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            persona_id: persona.id,
            candidate_name: candidateName.trim() || null,
            role_target: roleTarget.trim() || null,
            experience_summary: experienceSummary.trim() || null,
            resume_text: resumeText.trim() || null,
            jd_text: jdText.trim() || null,
            question_limit: q_limit,
          })
        });
        if (res.ok) break;
        lastErr = `HTTP ${res.status}`;
      } catch (err) {
        lastErr = err.message || 'Network error';
      }
      // brief backoff before retry
      await new Promise(r => setTimeout(r, 1500));
    }

    if (!res || !res.ok) {
      setStartError(`Couldn't start the interview (${lastErr || 'unknown error'}). Please try again.`);
      setIsProcessing(false);
      return;
    }

    try {
      const data = await res.json();
      setSessionId(data.session_id);
      setCurrentSpeech(data.greeting);
      setMessages([{ role: 'assistant', content: data.greeting }]);
      setQuestionLimit(data.question_limit || null);
      setQuestionsAsked(0);
      setStage('interview');
      setInterviewTime(0);
      
      if (data.audio_base64) {
        playAudio(data.audio_base64, true);
      } else {
        setTimeout(() => startListening(), 1000);
      }
    } catch (err) {
      setStartError("Couldn't read interview response. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  // Get feedback
  const getFeedback = async () => {
    stopListening();
    clearLongPauseTimer();
    setIsProcessing(true);
    
    try {
      const res = await fetch(`${API_URL}/api/interview/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId })
      });
      
      if (!res.ok) throw new Error('Feedback failed');
      const data = await res.json();
      
      setFeedback(data.feedback);
      setFeedbackData(data);
      setStage('feedback');
      
      if (data.audio_base64) {
        playAudio(data.audio_base64, false);
      }
    } catch (err) {
      console.error('Feedback error:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  // End interview
  const endInterview = async () => {
    stopListening();
    stopAudio();
    clearLongPauseTimer();
    
    try {
      await fetch(`${API_URL}/api/interview/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, user_text: 'End Interview' })
      });
    } catch (err) {
      console.error('End interview error:', err);
    }
    
    getFeedback();
  };

  // Reset
  const resetInterview = () => {
    stopListening();
    stopAudio();
    stopInterruptDetection();
    clearLongPauseTimer();
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setStage('start');
    setSelectedPersona(null);
    setSessionId(null);
    setMessages([]);
    setCurrentSpeech('');
    setFeedback(null);
    setFeedbackData(null);
    setInterviewTime(0);
    setListeningStatus('idle');
    setIsInterrupted(false);
    setCurrentSentiment(null);
    setConfidenceScore(null);
    setSentimentHistory([]);
    setVoiceAnalysis(null);
    setCandidateName('');
    setCandidateEmail('');
    setRoleTarget('');
    setExperienceSummary('');
    setResumeText('');
    setResumeFileName('');
    setJdText('');
    setJdFileName('');
    setQuestionMode('fixed');
    setCustomQuestionCount(10);
    setQuestionsAsked(0);
    setQuestionLimit(null);
    setDocError('');
    setStartError('');
    setMicError('');
  };

  // Get status message
  const getStatusMessage = () => {
    if (isInterrupted) return 'You interrupted — listening now...';
    if (isSpeaking) return 'Agent is speaking… (speak any time to interrupt)';
    if (isProcessing) return 'Got it — thinking…';
    if (listeningStatus === 'listening') {
      if (pushToTalkMode) return pttHeld ? 'Recording — release to send' : 'Mic open — hold the button to speak';
      return voiceActive ? 'Capturing your response…' : 'Mic is on — speak whenever you\'re ready';
    }
    if (micError) return micError;
    if (pushToTalkMode) return 'Push-to-Talk on — hold the button (or SPACE) to speak';
    return 'Mic is preparing — or tap "Tap to Speak" to start now';
  };

  // ==================== RENDER STAGES ====================

  // START SCREEN
  const renderStartScreen = () => (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0a1a] via-[#0d1025] to-[#0a0a1a] flex flex-col items-center justify-center p-6">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-gradient-radial from-blue-500/20 via-transparent to-transparent blur-3xl pointer-events-none" />
      
      <div className="mb-8 text-center">
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
          Practiq <span className="text-blue-400">Interview AI</span>
        </h1>
        <p className="text-white/50 text-sm">Powered by TaxSetu</p>
      </div>

      <button
        onClick={() => setStage('select')}
        data-testid="power-button"
        className="group relative w-32 h-32 md:w-40 md:h-40 mb-12 focus:outline-none"
      >
        <div className="absolute inset-0 rounded-full bg-blue-500/20 blur-2xl group-hover:bg-blue-400/30 transition-all duration-500" />
        <div className="absolute inset-2 rounded-full border-4 border-blue-500/50 group-hover:border-blue-400 transition-all duration-300" />
        <div className="absolute inset-4 rounded-full bg-gradient-to-br from-[#1a1a2e] to-[#0d0d1a] flex items-center justify-center
          group-hover:from-[#1e1e3a] group-hover:to-[#12122a] transition-all duration-300 shadow-2xl">
          <Power className="w-12 h-12 md:w-16 md:h-16 text-blue-400 group-hover:text-blue-300 transition-colors" />
        </div>
        <div className="absolute inset-0 rounded-full border-2 border-blue-400/0 group-hover:border-blue-400/50 
          group-hover:animate-ping transition-all duration-300 pointer-events-none" />
      </button>
      
      <p className="text-white/60 text-lg mb-12">Press To Start</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl">
        {[
          { num: '01', text: 'Select your interview expert from three specialists' },
          { num: '02', text: 'Speak naturally - mic auto-activates. Interrupt AI anytime!' },
          { num: '03', text: 'Get detailed AI analysis and improvement tips' }
        ].map((step, i) => (
          <div key={i} className="flex items-start gap-4 p-4">
            <span className="text-2xl font-bold text-blue-400/60">{step.num}</span>
            <p className="text-white/70 text-sm leading-relaxed">{step.text}</p>
          </div>
        ))}
      </div>
    </div>
  );

  // SELECTION SCREEN
  // SELECTION + SETUP — single split-pane page
  const renderSelectionScreen = () => {
    const persona = selectedPersona; // null until user picks
    const accent = persona?.orbColors || { primary: '#6b7280', secondary: '#374151', glow: 'rgba(107,114,128,0.25)' };
    const canStart = !!persona && !isProcessing && !parsingResume && !parsingJD;

    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0a0a1a] via-[#0d1025] to-[#0a0a1a] relative overflow-hidden">
        {/* ambient glow */}
        <div
          className="absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[500px] blur-3xl pointer-events-none transition-colors duration-700"
          style={{ background: `radial-gradient(ellipse, ${accent.glow}, transparent 70%)` }}
        />

        {/* Top bar */}
        <div className="relative z-10 flex items-center justify-between px-6 md:px-10 pt-6">
          <button
            onClick={() => { stopAudio(); setStage('start'); }}
            data-testid="select-back-btn"
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <div className="text-center hidden md:block">
            <h1 className="text-white font-semibold text-lg tracking-wide">
              Practiq <span className="text-blue-400">Interview AI</span>
            </h1>
            <p className="text-white/40 text-xs">Powered by TaxSetu</p>
          </div>
          <button
            onClick={() => { if (!isMuted) stopAudio(); setIsMuted(!isMuted); }}
            className="p-2.5 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
            data-testid="mute-toggle-select"
          >
            {isMuted ? <VolumeX className="w-5 h-5 text-white/60" /> : <Volume2 className="w-5 h-5 text-white" />}
          </button>
        </div>

        {/* Split panel layout */}
        <div className="relative z-10 px-4 md:px-10 pt-6 pb-12 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
            {/* LEFT — Interviewer list */}
            <div className="lg:col-span-5">
              <div className="mb-5">
                <h2 className="text-2xl md:text-3xl font-bold text-white">Choose Your Interviewer</h2>
                <p className="text-white/50 text-sm mt-1">Hover to preview • Click to select</p>
              </div>

              <div className="space-y-3" onMouseLeave={() => { introHoverIdRef.current = null; stopAudio(); }}>
                {Object.values(PERSONAS).map((p) => {
                  const isSelected = selectedPersona?.id === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => { setSelectedPersona(p); stopAudio(); }}
                      onMouseEnter={() => { setHoveredPersona(p); playIntroAudio(p); }}
                      data-testid={`interviewer-row-${p.id}`}
                      className={`w-full text-left flex items-center gap-4 p-4 rounded-2xl border transition-all duration-300 ${
                        isSelected
                          ? 'bg-white/5 shadow-lg scale-[1.01]'
                          : 'bg-white/[0.025] hover:bg-white/[0.06] border-white/10'
                      }`}
                      style={isSelected ? {
                        borderColor: p.orbColors.primary,
                        boxShadow: `0 8px 32px ${p.orbColors.glow}`,
                      } : {}}
                    >
                      {/* Avatar */}
                      <div
                        className="relative flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-full overflow-hidden ring-2 transition-all"
                        style={{ borderColor: isSelected ? p.orbColors.primary : 'rgba(255,255,255,0.15)' }}
                      >
                        <img src={p.avatar} alt={p.name} className="w-full h-full object-cover" />
                        {isSelected && (
                          <span
                            className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
                            style={{ background: p.orbColors.primary }}
                          >
                            ✓
                          </span>
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="text-white font-semibold text-base md:text-lg">{p.name}</h3>
                          <span
                            className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/10 text-white/80"
                          >
                            {p.voice}
                          </span>
                        </div>
                        <p className="text-white/70 text-xs md:text-sm">{p.title}</p>
                        <p className="text-white/40 text-xs mt-0.5">{p.subtitle}</p>
                        {/* gradient strip */}
                        <div
                          className="mt-2 h-0.5 w-12 rounded-full opacity-80"
                          style={{ background: `linear-gradient(to right, ${p.orbColors.primary}, ${p.orbColors.secondary})` }}
                        />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* RIGHT — Form panel */}
            <div className="lg:col-span-7">
              <div
                className={`relative rounded-2xl border bg-[#11121f]/70 backdrop-blur-md p-6 md:p-8 transition-all duration-500 ${
                  persona ? 'opacity-100' : 'opacity-60'
                }`}
                style={{ borderColor: persona ? `${accent.primary}40` : 'rgba(255,255,255,0.08)' }}
                data-testid="setup-panel"
              >
                {/* header */}
                <div className="flex items-start justify-between mb-5">
                  <div>
                    <h3 className="text-white text-lg md:text-xl font-semibold">
                      {persona ? `Set up your interview with ${persona.name}` : 'Set up your interview'}
                    </h3>
                    <p className="text-white/45 text-xs md:text-sm mt-1">
                      {persona
                        ? 'All fields are optional — the more we know, the better the questions.'
                        : 'Pick an interviewer on the left to begin.'}
                    </p>
                  </div>
                  {persona && (
                    <span
                      className="hidden md:inline-block text-[11px] uppercase tracking-wider px-2 py-1 rounded-md"
                      style={{ background: `${accent.primary}1f`, color: accent.primary }}
                      data-testid="selected-persona-chip"
                    >
                      {persona.name}
                    </span>
                  )}
                </div>

                {/* Personal Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                  <div>
                    <label className="block text-white/55 text-xs mb-1.5 uppercase tracking-wider">Name</label>
                    <input
                      type="text"
                      value={candidateName}
                      onChange={(e) => setCandidateName(e.target.value)}
                      placeholder="Aisha Khan"
                      data-testid="setup-name-input"
                      className="w-full px-3.5 py-2.5 bg-white/[0.04] border border-white/10 rounded-lg text-white placeholder-white/25 text-sm focus:outline-none focus:border-white/30 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-white/55 text-xs mb-1.5 uppercase tracking-wider">Email</label>
                    <input
                      type="email"
                      value={candidateEmail}
                      onChange={(e) => setCandidateEmail(e.target.value)}
                      placeholder="you@email.com"
                      data-testid="setup-email-input"
                      className="w-full px-3.5 py-2.5 bg-white/[0.04] border border-white/10 rounded-lg text-white placeholder-white/25 text-sm focus:outline-none focus:border-white/30 transition-colors"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-white/55 text-xs mb-1.5 uppercase tracking-wider">Target Role</label>
                    <input
                      type="text"
                      value={roleTarget}
                      onChange={(e) => setRoleTarget(e.target.value)}
                      placeholder="e.g. CA Articleship, Tax Analyst, Software Engineer"
                      data-testid="setup-role-input"
                      className="w-full px-3.5 py-2.5 bg-white/[0.04] border border-white/10 rounded-lg text-white placeholder-white/25 text-sm focus:outline-none focus:border-white/30 transition-colors"
                    />
                  </div>
                </div>

                {/* Resume + JD side by side */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                  {/* Resume */}
                  <label
                    htmlFor="resume-file"
                    data-testid="resume-upload-label"
                    className="cursor-pointer group flex flex-col items-start gap-1.5 px-4 py-3 rounded-lg border border-dashed border-white/15 bg-white/[0.025] hover:bg-white/[0.05] transition-colors"
                  >
                    <div className="flex items-center gap-2 text-white/85 text-sm font-medium">
                      {parsingResume
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <Upload className="w-4 h-4 text-white/60 group-hover:text-white" />}
                      <span>Upload Resume</span>
                      <span className="text-white/30 text-[11px] ml-1">PDF • DOCX</span>
                    </div>
                    {resumeFileName ? (
                      <p className="flex items-center gap-1 text-emerald-300/90 text-xs truncate w-full">
                        <FileText className="w-3 h-3" /> {resumeFileName}
                      </p>
                    ) : (
                      <p className="text-white/35 text-xs">Optional — drag-drop coming soon</p>
                    )}
                    <input
                      id="resume-file"
                      type="file"
                      accept=".pdf,.docx,.txt"
                      onChange={(e) => handleFileUpload(e.target.files?.[0], 'resume')}
                      className="hidden"
                      data-testid="resume-file-input"
                    />
                  </label>

                  {/* JD */}
                  <label
                    htmlFor="jd-file"
                    className="cursor-pointer group flex flex-col items-start gap-1.5 px-4 py-3 rounded-lg border border-dashed border-white/15 bg-white/[0.025] hover:bg-white/[0.05] transition-colors"
                  >
                    <div className="flex items-center gap-2 text-white/85 text-sm font-medium">
                      {parsingJD
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <Upload className="w-4 h-4 text-white/60 group-hover:text-white" />}
                      <span>Upload Job Description</span>
                      <span className="text-white/30 text-[11px] ml-1">PDF • DOCX</span>
                    </div>
                    {jdFileName ? (
                      <p className="flex items-center gap-1 text-emerald-300/90 text-xs truncate w-full">
                        <FileText className="w-3 h-3" /> {jdFileName}
                      </p>
                    ) : (
                      <p className="text-white/35 text-xs">Or paste the JD text below</p>
                    )}
                    <input
                      id="jd-file"
                      type="file"
                      accept=".pdf,.docx,.txt"
                      onChange={(e) => handleFileUpload(e.target.files?.[0], 'jd')}
                      className="hidden"
                      data-testid="jd-file-input"
                    />
                  </label>
                </div>

                {/* JD paste */}
                <details className="mb-4 group" data-testid="jd-paste-details">
                  <summary className="text-white/55 text-xs cursor-pointer hover:text-white/80 select-none flex items-center gap-1">
                    <span className="transition-transform group-open:rotate-90">›</span> Paste Job Description text instead
                  </summary>
                  <textarea
                    value={jdText}
                    onChange={(e) => setJdText(e.target.value)}
                    rows={3}
                    placeholder="Paste the JD here..."
                    data-testid="setup-jd-textarea"
                    className="mt-2 w-full px-3.5 py-2.5 bg-white/[0.04] border border-white/10 rounded-lg text-white placeholder-white/25 text-sm focus:outline-none focus:border-white/30 resize-none"
                  />
                </details>

                {/* Experience (only when no resume) */}
                {!resumeText && (
                  <div className="mb-4">
                    <label className="block text-white/55 text-xs mb-1.5 uppercase tracking-wider">
                      Profile & Experience <span className="text-white/30 normal-case">(used if no resume)</span>
                    </label>
                    <textarea
                      value={experienceSummary}
                      onChange={(e) => setExperienceSummary(e.target.value)}
                      rows={3}
                      placeholder="e.g. 2 years experience in GST compliance and TDS returns. Skilled in Tally, Excel. Pursuing CA Final."
                      data-testid="setup-experience-input"
                      className="w-full px-3.5 py-2.5 bg-white/[0.04] border border-white/10 rounded-lg text-white placeholder-white/25 text-sm focus:outline-none focus:border-white/30 resize-none"
                    />
                  </div>
                )}

                {/* Interview length */}
                <div className="mb-5">
                  <label className="block text-white/55 text-xs mb-2 uppercase tracking-wider">Interview Length</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { id: 'fixed', label: 'Standard', sub: '8–10 Qs' },
                      { id: 'custom', label: 'Custom', sub: 'pick count' },
                      { id: 'open', label: 'Open', sub: 'no limit' },
                    ].map(opt => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setQuestionMode(opt.id)}
                        data-testid={`q-mode-${opt.id}`}
                        className={`text-left px-3 py-2.5 rounded-lg border text-xs transition-all ${
                          questionMode === opt.id
                            ? 'text-white'
                            : 'bg-white/[0.03] border-white/10 text-white/60 hover:bg-white/[0.06]'
                        }`}
                        style={questionMode === opt.id ? {
                          background: `${accent.primary}1a`,
                          borderColor: `${accent.primary}88`,
                        } : {}}
                      >
                        <div className="font-medium text-sm">{opt.label}</div>
                        <div className="text-[10px] mt-0.5 opacity-70">{opt.sub}</div>
                      </button>
                    ))}
                  </div>
                  {questionMode === 'custom' && (
                    <div className="mt-2 flex items-center gap-2">
                      <input
                        type="number"
                        min={3}
                        max={25}
                        value={customQuestionCount}
                        onChange={(e) => setCustomQuestionCount(e.target.value)}
                        data-testid="custom-q-count"
                        className="w-20 px-2.5 py-1.5 bg-white/[0.04] border border-white/10 rounded text-white text-sm"
                      />
                      <span className="text-white/40 text-xs">questions (3–25)</span>
                    </div>
                  )}
                </div>

                {/* Errors */}
                {docError && (
                  <div className="mb-3 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-lg text-red-300 text-xs" data-testid="doc-error">
                    {docError}
                  </div>
                )}
                {startError && (
                  <div className="mb-3 px-3 py-2 bg-red-500/15 border border-red-500/40 rounded-lg text-red-200 text-sm" data-testid="start-error">
                    {startError}
                  </div>
                )}

                {/* Start CTA */}
                <button
                  onClick={() => persona && startInterview(persona)}
                  disabled={!canStart}
                  data-testid="setup-start-btn"
                  className="w-full mt-1 px-6 py-3.5 rounded-xl font-medium text-white text-sm md:text-base transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-lg"
                  style={{
                    background: persona
                      ? `linear-gradient(to right, ${accent.primary}, ${accent.secondary})`
                      : 'rgba(255,255,255,0.08)',
                    boxShadow: persona ? `0 8px 32px ${accent.glow}` : 'none',
                  }}
                >
                  {isProcessing
                    ? 'Connecting…'
                    : persona
                      ? `Start Interview with ${persona.name} →`
                      : 'Pick an interviewer to continue'}
                </button>

                <p className="text-center text-white/30 text-[11px] mt-3">
                  Your audio is processed in real-time. Nothing is stored without your consent.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // INTERVIEW SCREEN
  const renderInterviewScreen = () => {
    const persona = selectedPersona || PERSONAS.ms_spark;
    
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0a0a1a] via-[#0d1025] to-[#0a0a1a] flex flex-col">
        <div 
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] blur-3xl pointer-events-none transition-colors duration-1000"
          style={{ background: `radial-gradient(ellipse, ${isInterrupted ? 'rgba(239, 68, 68, 0.4)' : persona.orbColors.glow}, transparent 70%)` }}
        />
        
        {/* Header */}
        <header className="relative z-10 flex items-center justify-between px-6 py-4">
          <button 
            onClick={resetInterview}
            data-testid="exit-button"
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
            <span className="hidden sm:inline">Exit</span>
          </button>
          
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full transition-colors duration-300 ${
              isInterrupted ? 'bg-red-400 animate-pulse' :
              isSpeaking ? 'bg-blue-400 animate-pulse' : 
              isRecording ? 'bg-green-400 animate-pulse' : 
              'bg-white/30'
            }`} />
            <span className="text-white font-medium">{persona.name}</span>
            {questionLimit && (
              <span
                data-testid="q-counter"
                className="ml-3 px-2.5 py-0.5 text-xs rounded-full bg-white/10 border border-white/15 text-white/80"
              >
                Q {Math.min(questionsAsked, questionLimit)} / {questionLimit}
              </span>
            )}
          </div>
          
          <button
            onClick={() => setIsMuted(!isMuted)}
            data-testid="mute-toggle"
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
          >
            {isMuted ? <VolumeX className="w-5 h-5 text-white/60" /> : <Volume2 className="w-5 h-5 text-white" />}
          </button>
        </header>

        {/* Features Switchboard */}
        <div className="relative z-10 mx-auto mt-4">
          <div className="bg-[#1a1a2e]/80 backdrop-blur-sm rounded-2xl px-8 py-4 border border-white/10">
            <p className="text-center text-white/50 text-xs mb-3 tracking-wider">Features Switchboard</p>
            <div className="flex gap-8">
              <ToggleSwitch 
                label="Show Transcript" 
                checked={showTranscript}
                onChange={() => setShowTranscript(!showTranscript)}
                tooltip="Display conversation text"
              />
              <ToggleSwitch 
                label="Ideal Time Limit" 
                checked={idealTimeLimit}
                onChange={() => setIdealTimeLimit(!idealTimeLimit)}
                tooltip="Show recommended response time"
              />
              <ToggleSwitch 
                label="Push to Talk" 
                checked={pushToTalkMode}
                onChange={() => {
                  const next = !pushToTalkMode;
                  setPushToTalkMode(next);
                  if (next && isRecording) stopListening();
                }}
                tooltip="Hold the on-screen button (or SPACE) to speak — works best if auto-detect misses your voice"
                testId="ptt-toggle"
              />
            </div>
          </div>
        </div>

        {/* Main Content - Orb */}
        <main className="flex-1 flex flex-col items-center justify-center relative z-10">
          <AnimatedOrb 
            colors={persona.orbColors}
            isSpeaking={isSpeaking}
            isListening={isRecording}
            pulseIntensity={pulseIntensity}
            voiceActive={voiceActive}
            isInterrupted={isInterrupted}
          />
          
          {/* Listening Status Indicator */}
          <div className="mt-6 flex items-center gap-2">
            {isRecording && !isInterrupted && (
              <div className="flex items-center gap-2 px-4 py-2 bg-green-500/20 rounded-full border border-green-500/30">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                <span className="text-green-400 text-sm font-medium">
                  {voiceActive ? 'Capturing...' : 'Listening...'}
                </span>
              </div>
            )}
            {isInterrupted && (
              <div className="flex items-center gap-2 px-4 py-2 bg-red-500/20 rounded-full border border-red-500/30">
                <div className="w-2 h-2 bg-red-400 rounded-full animate-pulse" />
                <span className="text-red-400 text-sm font-medium">Interrupted!</span>
              </div>
            )}
          </div>
          
          {/* Sentiment Indicator */}
          {currentSentiment && !isRecording && !isSpeaking && (
            <div className="mt-4 flex flex-col items-center gap-2" data-testid="sentiment-indicator">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full border ${
                currentSentiment === 'confident' ? 'bg-emerald-500/20 border-emerald-500/30' :
                currentSentiment === 'nervous' ? 'bg-amber-500/20 border-amber-500/30' :
                currentSentiment === 'hesitant' ? 'bg-orange-500/20 border-orange-500/30' :
                'bg-gray-500/20 border-gray-500/30'
              }`}>
                <span className={`text-sm font-medium capitalize ${
                  currentSentiment === 'confident' ? 'text-emerald-400' :
                  currentSentiment === 'nervous' ? 'text-amber-400' :
                  currentSentiment === 'hesitant' ? 'text-orange-400' :
                  'text-gray-400'
                }`}>
                  {currentSentiment === 'confident' && '😊 '}
                  {currentSentiment === 'nervous' && '😰 '}
                  {currentSentiment === 'hesitant' && '🤔 '}
                  {currentSentiment === 'neutral' && '😐 '}
                  {currentSentiment}
                </span>
                {confidenceScore !== null && (
                  <span className="text-white/40 text-xs">
                    ({confidenceScore}%)
                  </span>
                )}
              </div>
              
              {/* Voice Metrics Mini Display */}
              {voiceAnalysis && voiceAnalysis.metrics && (
                <div className="flex items-center gap-3 text-xs text-white/40" data-testid="voice-metrics">
                  {voiceAnalysis.metrics.pitch && (
                    <span className="flex items-center gap-1">
                      🎵 {voiceAnalysis.metrics.pitch}Hz
                    </span>
                  )}
                  {voiceAnalysis.metrics.wpm && (
                    <span className="flex items-center gap-1">
                      ⚡ {voiceAnalysis.metrics.wpm} WPM
                    </span>
                  )}
                  {voiceAnalysis.metrics.pauses !== undefined && (
                    <span className="flex items-center gap-1">
                      ⏸️ {voiceAnalysis.metrics.pauses} pauses
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
        </main>

        {/* Timer */}
        <div className="relative z-10 text-center mb-4">
          <div className="w-16 h-0.5 bg-white/20 mx-auto mb-4" />
          <p className="text-3xl md:text-4xl font-light text-white tracking-wider">
            {formatTime(interviewTime)}
          </p>
        </div>

        {/* Status Message */}
        <div className="relative z-10 text-center mb-2">
          <p className="text-white/50 text-sm">{getStatusMessage()}</p>
        </div>

        {/* Live mic-level meter — visible whenever mic is recording so the user sees their voice is being heard */}
        {isRecording && (
          <div className="relative z-10 mb-3 flex items-center justify-center gap-1.5" data-testid="audio-level-meter">
            {[0, 1, 2, 3, 4, 5, 6].map((i) => {
              const level = Math.min(1, pulseIntensity * 3.2); // amplify for visibility
              const barOn = level > i / 7;
              return (
                <div
                  key={i}
                  className="w-1.5 rounded-full transition-all duration-75"
                  style={{
                    height: `${10 + (barOn ? 8 + i * 3 : 2)}px`,
                    background: barOn
                      ? (voiceActive ? '#34d399' : 'rgba(96,165,250,0.85)')
                      : 'rgba(255,255,255,0.12)',
                  }}
                />
              );
            })}
            <span className={`ml-2 text-xs ${voiceActive ? 'text-emerald-300' : 'text-white/40'}`}>
              {voiceActive ? 'Hearing you…' : 'Listening for your voice…'}
            </span>
          </div>
        )}

        {/* Thinking indicator with persona avatar — between user response and AI reply */}
        {isProcessing && (
          <div className="relative z-10 mb-3 flex items-center justify-center gap-3" data-testid="thinking-indicator">
            <img
              src={persona.avatar}
              alt={persona.name}
              className="w-9 h-9 rounded-full object-cover ring-2"
              style={{ borderColor: persona.orbColors.primary }}
            />
            <div className="flex items-center gap-1.5">
              <span className="text-white/80 text-sm">{persona.name} is thinking</span>
              <span className="flex gap-0.5">
                <span className="w-1 h-1 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1 h-1 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: '120ms' }} />
                <span className="w-1 h-1 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: '240ms' }} />
              </span>
            </div>
          </div>
        )}

        {/* Agent Speech + User's last reply (always visible — voice + text) */}
        <div className="relative z-10 px-6 pb-32 text-center" data-testid="agent-speech-container">
          <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto leading-relaxed" data-testid="agent-speech">
            <span className="text-white/50">Agent:</span> {currentSpeech}
          </p>
          {(() => {
            const lastUser = [...messages].reverse().find(m => m.role === 'user');
            if (!lastUser) return null;
            return (
              <p
                className="mt-4 text-sm md:text-base text-blue-200/85 max-w-3xl mx-auto leading-relaxed italic"
                data-testid="user-last-response"
              >
                <span className="text-blue-300/60 not-italic">You:</span> {lastUser.content}
              </p>
            );
          })()}

          {/* Push-to-Talk HOLD button — primary control when PTT mode is on */}
          {pushToTalkMode && !isSpeaking && !isProcessing && (
            <button
              type="button"
              data-testid="push-to-talk-btn"
              onMouseDown={() => { pttHeldRef.current = true; setPttHeld(true); setMicError(''); if (!isRecording) startListening(); }}
              onMouseUp={() => { pttHeldRef.current = false; setPttHeld(false); if (isRecording) stopListening(); }}
              onMouseLeave={() => { if (pttHeldRef.current) { pttHeldRef.current = false; setPttHeld(false); if (isRecording) stopListening(); } }}
              onTouchStart={(e) => { e.preventDefault(); pttHeldRef.current = true; setPttHeld(true); setMicError(''); if (!isRecording) startListening(); }}
              onTouchEnd={(e) => { e.preventDefault(); pttHeldRef.current = false; setPttHeld(false); if (isRecording) stopListening(); }}
              className={`mt-6 inline-flex items-center gap-2 px-7 py-3 rounded-full text-sm md:text-base font-medium text-white border backdrop-blur-sm transition-all shadow-lg select-none ${
                pttHeld
                  ? 'bg-emerald-500/40 border-emerald-300/60 scale-105'
                  : 'bg-blue-500/25 hover:bg-blue-500/35 border-blue-400/50'
              }`}
              style={{ boxShadow: pttHeld ? '0 8px 32px rgba(52,211,153,0.35)' : '0 8px 24px rgba(96,165,250,0.18)' }}
            >
              <span className={`w-2.5 h-2.5 rounded-full ${pttHeld ? 'bg-emerald-200 animate-pulse' : 'bg-blue-200'}`} />
              {pttHeld ? 'Listening — release to send' : 'Hold to Speak'}
            </button>
          )}

          {/* Tap-to-Speak fallback — only when NOT in PTT mode and mic isn't already on */}
          {!pushToTalkMode && !isSpeaking && !isProcessing && !isRecording && (
            <button
              type="button"
              onClick={() => { setMicError(''); startListening(); }}
              data-testid="tap-to-speak-btn"
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium text-white bg-blue-500/20 hover:bg-blue-500/30 border border-blue-400/40 backdrop-blur-sm transition-colors shadow-lg"
              style={{ boxShadow: '0 8px 24px rgba(96,165,250,0.18)' }}
            >
              <span className="w-2 h-2 rounded-full bg-blue-300 animate-pulse" />
              Tap to Speak
            </button>
          )}
        </div>

        {/* Transcript Panel */}
        {showTranscript && messages.length > 1 && (
          <div className="fixed bottom-24 right-6 w-80 max-h-64 overflow-y-auto bg-[#1a1a2e]/90 backdrop-blur-sm 
            rounded-xl border border-white/10 p-4 z-20">
            <p className="text-white/50 text-xs mb-3">Transcript</p>
            <div className="space-y-3">
              {messages.slice(-6).map((msg, i) => (
                <div key={i} className={`text-sm ${msg.role === 'user' ? 'text-blue-300' : 'text-white/80'}`}>
                  <span className="text-white/40">{msg.role === 'user' ? 'You:' : 'Agent:'}</span> {msg.content}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* End Interview Button */}
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-20">
          <button
            onClick={endInterview}
            disabled={isProcessing || messages.length < 2}
            data-testid="end-interview-button"
            className="px-8 py-3 bg-white/10 hover:bg-red-500/20 hover:border-red-500/50 rounded-full text-white/80 
              hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed
              border border-white/10"
          >
            End Interview
          </button>
        </div>
      </div>
    );
  };

  // FEEDBACK SCREEN
  const renderFeedbackScreen = () => {
    const persona = selectedPersona || PERSONAS.ms_spark;
    
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0a0a1a] via-[#0d1025] to-[#0a0a1a] flex flex-col items-center justify-center p-6">
        <div 
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] blur-3xl pointer-events-none"
          style={{ background: `radial-gradient(ellipse, ${persona.orbColors.glow}, transparent 70%)` }}
        />

        <div className="relative z-10 w-full max-w-2xl">
          <div className="text-center mb-8">
            <div 
              className="w-24 h-24 mx-auto mb-4 rounded-full overflow-hidden ring-4"
              style={{ ringColor: persona.orbColors.primary }}
            >
              <img src={persona.avatar} alt={persona.name} className="w-full h-full object-cover" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">Interview Complete</h2>
            <p className="text-white/50">Feedback from {persona.name}</p>
            <p className="text-white/30 text-sm mt-2">Duration: {formatTime(interviewTime).replace('-', '')}</p>
          </div>

          <div className="bg-[#1a1a2e]/80 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-white/10 mb-6">
            {/* Overall score */}
            {feedbackData?.overall_score != null && (
              <div className="flex items-center justify-between mb-5 pb-5 border-b border-white/10" data-testid="overall-score">
                <div>
                  <p className="text-white/50 text-xs uppercase tracking-wider">Overall Score</p>
                  <p className="text-4xl font-bold text-white mt-1">{feedbackData.overall_score}<span className="text-lg text-white/40">/100</span></p>
                </div>
                <div className="text-right">
                  <p className="text-white/50 text-xs uppercase tracking-wider">Average Confidence</p>
                  <p className="text-2xl font-semibold text-white mt-1">
                    {sentimentHistory.length > 0
                      ? Math.round(sentimentHistory.reduce((s, h) => s + (h.confidence || 50), 0) / sentimentHistory.length)
                      : 50}%
                  </p>
                </div>
              </div>
            )}

            <p className="text-white/90 leading-relaxed whitespace-pre-wrap mb-4" data-testid="feedback-content">
              {feedback}
            </p>

            {/* Strengths / Weaknesses / Tips */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              {feedbackData?.strengths?.length > 0 && (
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4" data-testid="feedback-strengths">
                  <p className="text-emerald-300 text-xs uppercase tracking-wider mb-2">Strengths</p>
                  <ul className="space-y-1.5">
                    {feedbackData.strengths.map((s, i) => (
                      <li key={i} className="text-white/85 text-sm leading-snug">• {s}</li>
                    ))}
                  </ul>
                </div>
              )}
              {feedbackData?.weaknesses?.length > 0 && (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4" data-testid="feedback-weaknesses">
                  <p className="text-amber-300 text-xs uppercase tracking-wider mb-2">Areas to Improve</p>
                  <ul className="space-y-1.5">
                    {feedbackData.weaknesses.map((s, i) => (
                      <li key={i} className="text-white/85 text-sm leading-snug">• {s}</li>
                    ))}
                  </ul>
                </div>
              )}
              {feedbackData?.improvement_tips?.length > 0 && (
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4" data-testid="feedback-tips">
                  <p className="text-blue-300 text-xs uppercase tracking-wider mb-2">Improvement Tips</p>
                  <ul className="space-y-1.5">
                    {feedbackData.improvement_tips.map((s, i) => (
                      <li key={i} className="text-white/85 text-sm leading-snug">• {s}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Per-question ratings */}
            {feedbackData?.per_question?.length > 0 && (
              <div className="mt-6 pt-6 border-t border-white/10" data-testid="per-question-feedback">
                <p className="text-white/60 text-xs uppercase tracking-wider mb-3">Question-by-Question Breakdown</p>
                <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                  {feedbackData.per_question.map((q, i) => (
                    <div key={i} className="bg-white/5 border border-white/10 rounded-lg p-3">
                      <div className="flex items-start justify-between gap-3">
                        <p className="text-white/90 text-sm font-medium flex-1">Q{i + 1}. {q.question}</p>
                        <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-semibold ${
                          q.rating >= 4 ? 'bg-emerald-500/20 text-emerald-300' :
                          q.rating === 3 ? 'bg-amber-500/20 text-amber-300' :
                          'bg-red-500/20 text-red-300'
                        }`}>
                          {q.rating}/5
                        </span>
                      </div>
                      {q.answer && (
                        <p className="text-white/50 text-xs mt-2 italic">Your answer: {q.answer}</p>
                      )}
                      {q.comment && (
                        <p className="text-white/70 text-xs mt-2">{q.comment}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={resetInterview}
              data-testid="try-again-button"
              className="px-8 py-3 rounded-full font-medium transition-all"
              style={{ 
                background: `linear-gradient(to right, ${persona.orbColors.primary}, ${persona.orbColors.secondary})`,
                color: 'white'
              }}
            >
              Try Another Interview
            </button>
            <button
              onClick={() => window.location.href = '/'}
              className="px-8 py-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-all
                border border-white/10 hover:border-white/20"
            >
              Back to Home
            </button>
          </div>
        </div>

        <p className="absolute bottom-6 text-white/30 text-sm">Powered by Practiq • TaxSetu AI</p>
      </div>
    );
  };

  // MAIN RENDER
  return (
    <>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes ripple-1 {
          0% { transform: scale(1); opacity: 0.6; }
          100% { transform: scale(1.5); opacity: 0; }
        }
        @keyframes ripple-2 {
          0% { transform: scale(1); opacity: 0.4; }
          100% { transform: scale(1.8); opacity: 0; }
        }
        @keyframes ripple-3 {
          0% { transform: scale(1); opacity: 0.2; }
          100% { transform: scale(2.1); opacity: 0; }
        }
        .animate-ripple-1 {
          animation: ripple-1 2s ease-out infinite;
        }
        .animate-ripple-2 {
          animation: ripple-2 2s ease-out infinite 0.3s;
        }
        .animate-ripple-3 {
          animation: ripple-3 2s ease-out infinite 0.6s;
        }
        @keyframes voice-bar {
          0%, 100% { transform: scaleY(0.3); }
          50% { transform: scaleY(1); }
        }
        .animate-voice-bar {
          animation: voice-bar 0.5s ease-in-out infinite;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
      
      {stage === 'start' && renderStartScreen()}
      {stage === 'select' && renderSelectionScreen()}
      {stage === 'interview' && renderInterviewScreen()}
      {stage === 'feedback' && renderFeedbackScreen()}
    </>
  );
}
